import { BsEye, BsEyeSlash } from 'react-icons/bs';
import { extractInitials, buildGeriatricoMarkerIcon } from './geriatricsHelpers';
import { geocodingServices, houseToHouseServices, hthHelper, mapServices } from 'src/services';
import { GeriatricsForm } from './subcomponents/GeriatricsForm';
import { GeriatricsTable } from './subcomponents/GeriatricsTable';
import { getTerritoryColor } from './mapConstants';
import { GoogleMap, Marker, useJsApiLoader, InfoWindow } from '@react-google-maps/api';
import { googleMapConfig, isLocalhost } from 'src/app-config';
import { H2, Loading } from 'src/components/_commons';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { typeCoords, typeHTHTerritory, typeHTHFace, typeMapGeriatrico, typeMapToast } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useGetIsDarkMode, useGetIsMobile } from 'src/custom-hooks';
import { useNavigate } from 'react-router';
import GeriatricBlockPolygon from './subcomponents/GeriatricBlockPolygon';
import JSZip from 'jszip';
import React from 'react';

const initialForm = {
    activo: true,
    diasHorarios: '',
    direccion: '',
    estado: '',
    hermanosAsignados: ['', '', '', ''],
    nombre: '',
    telefono: '',
    territorio: ''
};

export const AdminsGeriatricsMap = () => {
    const [centerCoords, setCenterCoords] = useState<typeCoords | null>(null);
    const [hthTerritories, setHTHTerritories] = useState<typeHTHTerritory[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isGeolocationReady, setIsGeolocationReady] = useState(false);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const dispatch = useDispatch();
    const isMobile = useGetIsMobile();
    const isDarkMode = useGetIsDarkMode();
    const map = useRef<any>(null);
    const mapLoader = useJsApiLoader(googleMapConfig);
    const navigate = useNavigate();
    const [geriatricos, setGeriatricos] = useState<typeMapGeriatrico[]>([]);
    const [form, setForm] = useState(initialForm);
    const [adding, setAdding] = useState(false);
    const [geocodingLoading, setGeocodingLoading] = useState(false);
    const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
    const [selectedGeriatrico, setSelectedGeriatrico] = useState<typeMapGeriatrico | null>(null);
    const [editId, setEditId] = useState<number | null>(null);
    const [showNumbers, setShowNumbers] = useState(true);
    const [showGeriatrics, setShowGeriatrics] = useState(true);
    const [toast, setToast] = useState<typeMapToast|null>(null);
    const [loadingMapData, setLoadingMapData] = useState(true);
    const [loadingGeriatrico, setLoadingGeriatrico] = useState(false);
    const [importingInfo, setImportingInfo] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const openModalHandler = (territoryNumber: string) => {
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: "Confirmar",
            message: `¿Ir al territorio ${territoryNumber}?`,
            execution: () => {
                navigate(`/casa-en-casa/${territoryNumber}`);
            }
        }));
    }

    useEffect(() => {
        if (!navigator.geolocation) {
            dispatch(setValuesAndOpenAlertModalReducer({
                mode: 'alert',
                title: "Error",
                message: "La aplicación no tiene permisos del navegador para usar la ubicación"
            }));
            setIsLoading(false);
            return;
        }
        navigator.geolocation.getCurrentPosition((geoPosition: GeolocationPosition) => {
            const position: typeCoords = {
                lat: isLocalhost ? -34.632126 : geoPosition.coords.latitude,
                lng: isLocalhost ? -58.456176 : geoPosition.coords.longitude
            };
            setCenterCoords(position);
            setIsGeolocationReady(true);
        }, (error) => {
            console.error('Error de geolocalización:', error);
            dispatch(setValuesAndOpenAlertModalReducer({
                mode: 'alert',
                title: "Error de ubicación",
                message: "No se pudo obtener tu ubicación. Verifica los permisos del navegador."
            }));
            setIsLoading(false);
        });
    }, [dispatch]);

    useEffect(() => {
        if (hthTerritories || !isGeolocationReady) {
            return;
        }
        houseToHouseServices.GetTerritoriesForMap().then(response => {
            setIsLoading(false);
            if (!response.success || !response.data?.hthTerritories) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setHTHTerritories(response.data.hthTerritories);
        });
    }, [isGeolocationReady]);

    useEffect(() => {
        const loadMapData = async () => {
            const response = await mapServices.Get();
            setLoadingMapData(false);
            if (!response.success || !response.data?.mapDoc) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setGeriatricos(response.data.mapDoc.geriatricos);
        }
        if (isGeolocationReady) {
            loadMapData();
        }
    }, [isGeolocationReady, refreshCounter]);

    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 2000);
            return () => clearTimeout(t);
        }
    }, [toast]);

    const handleAddGeriatrico = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoadingGeriatrico(true);
        setGeocodingLoading(true);
        let lat: number | null = null, lng: number | null = null;
        const response = await geocodingServices.GetCoordinatesFromAddress(form.direccion);
        setGeocodingLoading(false);
        if (response.success && response.data?.coordinates?.lat && response.data?.coordinates?.lng) {
            lat = response.data.coordinates.lat;
            lng = response.data.coordinates.lng;
        }
        const hermanosAsignados = form.hermanosAsignados.filter(h => h.trim() !== '');
        if (editId) {
            const geriatricoToUpdate = geriatricos.find(f => f.id === editId);
            if (geriatricoToUpdate) {
                const updatedGeriatrico = { 
                    ...geriatricoToUpdate, 
                    ...form, 
                    lat: lat || geriatricoToUpdate.lat, 
                    lng: lng || geriatricoToUpdate.lng,
                    hermanosAsignados
                };
                const response = await mapServices.UpdateGeriatrico(updatedGeriatrico);
                setLoadingGeriatrico(false);
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                setToast({ msg: 'Geriátrico modificado exitosamente', type: 'success' });
                setRefreshCounter(x => x + 1);
            }
            setLoadingGeriatrico(false);
        } else {
            const nuevoGeriatrico: typeMapGeriatrico = {
                id: 0,
                nombre: form.nombre,
                direccion: form.direccion,
                diasHorarios: form.diasHorarios,
                activo: form.activo,
                lat,
                lng,
                hermanosAsignados,
                estado: form.estado,
                telefono: form.telefono,
                territorio: form.territorio
            };
            const response = await mapServices.AddGeriatrico(nuevoGeriatrico);
            setLoadingGeriatrico(false);
            if (!response.success) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setToast({ msg: 'Geriátrico agregado exitosamente', type: 'success' });
            setRefreshCounter(x => x + 1);
        }
        setAdding(false);
        setEditId(null);
        setForm(initialForm);
    }

    const handleDeleteGeriatrico = async (id: number) => {
        setLoadingGeriatrico(true);
        dispatch(showLoadingModalReducer());
        const response = await mapServices.DeleteGeriatrico(id);
        dispatch(hideLoadingModalReducer());
        setLoadingGeriatrico(false);
        if (response.success) {
            setGeriatricos(prev => prev.filter(f => f.id !== id));
            setToast({ msg: 'Geriátrico eliminado exitosamente', type: 'success' });
        }
    }

    const handleToggleActivo = async (id: number) => {
        setLoadingGeriatrico(true);
        const geriatricoToUpdate = geriatricos.find(f => f.id === id);
        if (geriatricoToUpdate) {
            const updatedGeriatrico = {
                ...geriatricoToUpdate,
                activo: !geriatricoToUpdate.activo
            };
            dispatch(showLoadingModalReducer());
            const response = await mapServices.UpdateGeriatrico(updatedGeriatrico);
            dispatch(hideLoadingModalReducer());
            setLoadingGeriatrico(false);
            if (response.success) {
                setGeriatricos(prev => prev.map(f => f.id === id ? updatedGeriatrico : f));
                setToast({ msg: 'Estado de geriátrico actualizado', type: 'success' });
            } else {
                dispatch(setAlertResponse({ response }));
            }
        }
    }

    const handleImportKMZ = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }
        setLoadingGeriatrico(true);
        setImportingInfo("Procesando archivo KMZ...");
        try {
            const zip = await JSZip.loadAsync(file);
            const kmlFile = Object.keys(zip.files).find(name => name.endsWith('.kml'));
            if (!kmlFile) {
                return;
            }
            const kmlText = await zip.file(kmlFile)!.async('string');
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(kmlText, 'text/xml');
            const placemarks = xmlDoc.getElementsByTagName('Placemark');
            const currentGeriatricos = [...geriatricos];
            let addedCount = 0;
            for (let i = 0; i < placemarks.length; i++) {
                const p = placemarks[i];
                const name = p.getElementsByTagName('name')[0]?.textContent || 'Sin nombre';
                const coordString = p.getElementsByTagName('coordinates')[0]?.textContent || '';
                if (coordString) {
                    const [lng, lat] = coordString.trim().split(',').map(Number);
                    if (!isNaN(lat) && !isNaN(lng)) {
                        setImportingInfo(`Geocodificando ${i + 1} de ${placemarks.length}: ${name}`);
                        let direccion = 'Dirección no encontrada';
                        const response = await geocodingServices.GetAddressFromCoordinates({ lat, lng });
                        if (response.success && response.data?.address) {
                            direccion = response.data.address;
                        }
                        const existing = direccion !== 'Dirección no encontrada'
                            ? currentGeriatricos.find(g => g.direccion.toLowerCase().trim() === direccion.toLowerCase().trim())
                            : null;
                        if (!existing) {
                            const nuevo: typeMapGeriatrico = {
                                id: 0,
                                nombre: name,
                                direccion: direccion,
                                diasHorarios: '',
                                activo: true,
                                lat,
                                lng,
                                hermanosAsignados: [],
                                estado: '',
                                telefono: '',
                                territorio: ''
                            };
                            const response = await mapServices.AddGeriatrico(nuevo);
                            if (response.success) {
                                currentGeriatricos.push(nuevo);
                                addedCount++;
                            }
                        }
                    }
                }
            }
            setGeriatricos(currentGeriatricos);
            setToast({ msg: addedCount > 0 ? `${addedCount} geriátricos agregados` : 'No se encontraron nuevos geriátricos', type: 'info' });
        } catch (error) {
            console.error('Error al importar KMZ:', error);
        } finally {
            setLoadingGeriatrico(false);
            setImportingInfo(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    if (!mapLoader.isLoaded || isLoading || loadingMapData) {
        return (
            <div className="container">
                <H2 title="GERIÁTRICOS" />
                <Loading mt="80px" />
            </div>
        );
    }

    if (mapLoader.loadError) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">
                    Error al cargar Google Maps
                </div>
            </div>
        );
    }

    if (!centerCoords) {
        return (
            <div className="container mt-5">
                <div className="alert alert-warning">
                    Obteniendo ubicación...
                </div>
            </div>
        );
    }

    return (
        <div className={`container ${isDarkMode ? 'text-white' : ''} mt-5 pb-5`}>
            <H2 title={'GERIÁTRICOS'} />
            
            {toast && (
                <div className={`alert alert-${toast.type} position-fixed top-0 start-50 translate-middle-x mt-3 shadow`} style={{zIndex: 9999, minWidth: 250}}>
                    {toast.msg}
                </div>
            )}

            <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
              <button className={`btn btn-sm ${showNumbers ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setShowNumbers(v => !v)}>
                {showNumbers ? <BsEyeSlash className="me-1" /> : <BsEye className="me-1" />}
                {showNumbers ? 'Ocultar números' : 'Mostrar números'}
              </button>
              <button className={`btn btn-sm ${showGeriatrics ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setShowGeriatrics(v => !v)}>
                {showGeriatrics ? <BsEyeSlash className="me-1" /> : <BsEye className="me-1" />}
                {showGeriatrics ? 'Ocultar geriátricos' : 'Mostrar geriátricos'}
              </button>
            </div>
 
            <div className={`mt-3 ${isDarkMode ? 'text-dark' : ''}`}>
                <GoogleMap
                    center={centerCoords}
                    id={googleMapConfig.id}
                    mapContainerClassName={isMobile ? 'position-relative' : 'd-block m-auto'}
                    mapContainerStyle={{ height: isMobile ? '600px' : '700px', width: '100%', borderRadius: '12px', border: isDarkMode ? '1px solid #495057' : '1px solid #dee2e6' }}
                    onLoad={mapInstance => {map.current = mapInstance}}
                    options={{ disableDefaultUI: false, zoomControl: true, mapTypeControl: false, streetViewControl: false, fullscreenControl: true, rotateControl: true, gestureHandling: 'greedy' }}
                    zoom={17.8}
                >
                    {hthTerritories?.map((territory) => {
                        const territoryColor = getTerritoryColor(territory.territoryNumber);
                        const blocks = new Map<string, typeHTHFace[]>();
                        territory.faces.forEach(p => {
                            if (!blocks.has(p.block)) {
                                blocks.set(p.block, []);
                            }
                            blocks.get(p.block)!.push(p);
                        });
                        return Array.from(blocks.entries()).map(([block, blockPolygons], blockIndex) => {
                            const blockCoordinates = hthHelper.GetBlockCoordinates(blockPolygons, block);
                            return (
                                <GeriatricBlockPolygon
                                    key={`${territory.territoryNumber}-${block}`}
                                    path={blockCoordinates}
                                    color={territoryColor}
                                    strokeColor={territoryColor}
                                    isDarkMode={isDarkMode}
                                    onClick={() => openModalHandler(territory.territoryNumber)}
                                    label={showNumbers && blockIndex === 0 ? territory.territoryNumber : ''}
                                    center={hthHelper.GetBlockCenter(blockCoordinates)}
                                />
                            );
                        });
                    })}

                    {showGeriatrics && geriatricos.filter(f => f.activo && f.lat && f.lng).map(f => (
                      <Marker
                        key={f.id}
                        position={{ lat: f.lat!, lng: f.lng! }}
                        options={{ icon: buildGeriatricoMarkerIcon({ initials: extractInitials(f.nombre), active: f.activo }) }}
                        onClick={() => setSelectedGeriatrico(f)}
                      />
                    ))}

                    {selectedGeriatrico && (
                      <InfoWindow position={{ lat: selectedGeriatrico.lat!, lng: selectedGeriatrico.lng! }} onCloseClick={() => setSelectedGeriatrico(null)}>
                        <div>
                          <strong>{selectedGeriatrico.nombre}</strong><br />
                          {selectedGeriatrico.direccion}<br />
                          {selectedGeriatrico.diasHorarios && <>{selectedGeriatrico.diasHorarios}<br /></>}
                          {selectedGeriatrico.telefono && <div className="mt-1">📞 <a href={`tel:${selectedGeriatrico.telefono}`} className="text-decoration-none">{selectedGeriatrico.telefono}</a></div>}
                          {selectedGeriatrico.hermanosAsignados?.length ? <><br /><strong>Hermanos:</strong><br />{selectedGeriatrico.hermanosAsignados.join(', ')}</> : null}
                        </div>
                      </InfoWindow>
                    )}
                </GoogleMap>
            </div>

            {adding && (
                <GeriatricsForm 
                    form={form}
                    onFormChange={setForm}
                    onSubmit={handleAddGeriatrico}
                    onCancel={() => {
                        setAdding(false);
                        setEditId(null);
                        setForm(initialForm);
                    }}
                    editId={editId}
                    loading={loadingGeriatrico}
                    geocodingLoading={geocodingLoading}
                    isDarkMode={isDarkMode}
                    setAutocomplete={setAutocomplete}
                    autocomplete={autocomplete}
                />
            )}

            <GeriatricsTable 
                geriatricos={geriatricos}
                onEdit={(g) => {
                    const hermanos = g.hermanosAsignados && g.hermanosAsignados.length > 0 
                        ? [...g.hermanosAsignados, '', '', '', ''].slice(0, 4) 
                        : ['', '', '', ''];
                    setForm({
                        nombre: g.nombre,
                        direccion: g.direccion,
                        diasHorarios: g.diasHorarios || '',
                        activo: g.activo,
                        hermanosAsignados: hermanos,
                        estado: g.estado || '',
                        telefono: g.telefono || '',
                        territorio: g.territorio || ''
                    });
                    setEditId(g.id);
                    setAdding(true);
                    // window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onDelete={handleDeleteGeriatrico}
                onToggleActivo={handleToggleActivo}
                onAddManual={() => {
                    setForm(initialForm);
                    setEditId(null);
                    setAdding(true);
                }}
                onImportKMZ={() => fileInputRef.current?.click()}
                loading={loadingGeriatrico}
                importingInfo={importingInfo}
            />

            <input type="file" ref={fileInputRef} onChange={handleImportKMZ} accept=".kmz" style={{ display: 'none' }} />
        </div>
    );
}
